import React, {useEffect, useRef, useState} from "react"
import classNames from "classnames"
import TitleBar from "../../components/TitleBar/TitleBar"
import {MainSectionTable} from "../../containers/MainSection/MainSection"
import {useIsBankUser, useIsOrgUnitPilot, useIsUnitUser} from "../../services/auth"
import {Applications} from "../../components/Applications/Applications"
import {ProductApplications} from "../../components/ProductApplications/ProductApplications"
import {TutorialModalProps} from "../../components/Tutorial/TutorialModal"
import {useRefreshToken} from "../../hooks/useRefreshToken"
import {TutorialButton} from "../../components/Tutorial/TutorialButton"
import {generateCreateApplicationRequest} from "../../components/Tutorial/TutorialPayloads"
import {useQueryState} from "use-location-state"
import {ApplicationsColumns} from "../../components/Applications/Application.types"
import {TabsMeta, TitleBarTabs} from "../../components/TitleBarTabs/TitleBarTabs"
import {useCanAccessProductApplications, useCanAccessUnifiedApplications} from "../../services/permission"
import {ProductApplicationsColumns} from "../../resources/productApplication"
import {UnifiedApplications} from "../../components/UnifiedApplications/UnifiedApplications"
import {UnifiedApplicationsColumns} from "../../resources/unifiedApplication"

function Limit({current, limit}: {current: number; limit: number}) {
	return (
		<strong>
			<span className={classNames("current", {"at-limit": current >= limit})}>{current}</span> / {limit}
		</strong>
	)
}

function UnitPilotLimits({
	nonDeniedIndividualApplicationsNum,
	nonDeniedBusinessApplicationsNum,
}: {
	nonDeniedIndividualApplicationsNum: number
	nonDeniedBusinessApplicationsNum: number
}) {
	return (
		<div className="unit-pilot-limits">
			<div className="limits-box">
				<span>Non-declined individual applications</span>
				<Limit limit={1} current={nonDeniedIndividualApplicationsNum} />
			</div>
			<div className="limits-box">
				<span>Non-declined business applications</span>
				<Limit limit={1} current={nonDeniedBusinessApplicationsNum} />
			</div>
		</div>
	)
}

enum ApplicationTabs {
	OnboardingApplication = "onboardingApplication",
	ProductApplication = "productApplication",
	UnifiedApplication = "unifiedApplication",
}

export function ApplicationsPage() {
	const [nonDeniedIndividualApplicationsNum, setNonDeniedIndividualApplicationsNum] = useState<number>(0)
	const [nonDeniedBusinessApplicationsNum, setNonDeniedBusinessApplicationsNum] = useState<number>(0)
	const [payload, setPayload] = useState(generateCreateApplicationRequest)
	const [token, refresh] = useRefreshToken()
	const [showTutorial, setShowTutorial] = useQueryState("showTutorial", false)

	const ref = useRef(null)

	const [currentTab, setCurrentTab] = useQueryState<ApplicationTabs>("tab", ApplicationTabs.OnboardingApplication)

	const canAccessProductApps = useCanAccessProductApplications()
	const canAccessUnifiedApps = useCanAccessUnifiedApplications()
	const liClassName = "applications-tab-li"

	const tabs: TabsMeta<ApplicationTabs>[] = [
		{label: "Onboarding Applications", key: ApplicationTabs.OnboardingApplication, liClassName},
		...(canAccessProductApps
			? [
					{
						label: "Product Applications",
						key: ApplicationTabs.ProductApplication,
						liClassName,
					},
			  ]
			: []),
		...(canAccessUnifiedApps
			? [
					{
						label: "Unified Applications",
						key: ApplicationTabs.UnifiedApplication,
						liClassName,
					},
			  ]
			: []),
	]

	const showTabs = tabs.length > 1

	// If user doesn't have access and tries to view a gated tab, redirect to onboarding
	useEffect(() => {
		if (
			(!canAccessProductApps && currentTab === ApplicationTabs.ProductApplication) ||
			(!canAccessUnifiedApps && currentTab === ApplicationTabs.UnifiedApplication)
		) {
			setCurrentTab(ApplicationTabs.OnboardingApplication)
		}
	}, [canAccessProductApps, canAccessUnifiedApps, currentTab, setCurrentTab])

	const tutorialProps: TutorialModalProps = {
		tooltipId: "applicationTutorial",
		parentRef: ref,

		actions: [
			{
				actionName: "Create Application",
				title: "Test Create Application API",
				description: "Create an application. Once approved, a customer is automatically created",
				successMessage: "Success! You can now see your new application and customer in the dashboard",
				errorMessage: "There was an error creating the application",
				request: {
					method: "POST",
					path: "applications",
					payload: payload,
					headers: [
						{headerKey: "Content-Type", headerValue: "application/vnd.api+json"},
						{headerKey: "Cache-Control", headerValue: "no-cache"},
					],
				},
				refreshFunction: () => {
					refresh()
					setShowTutorial(false)
				},
				onSendAnotherRequest: () => {
					setPayload(generateCreateApplicationRequest())
				},
			},
		],
	}
	return (
		<div className="applications-page">
			<TitleBar title={"Applications"} className={"is-justify-content-flex-start"} noBorder>
				<div className="is-flex is-justify-content-flex-start is-align-items-center">
					{useIsOrgUnitPilot() ? (
						<UnitPilotLimits
							nonDeniedIndividualApplicationsNum={nonDeniedIndividualApplicationsNum}
							nonDeniedBusinessApplicationsNum={nonDeniedBusinessApplicationsNum}
						/>
					) : null}
					<TutorialButton tutorialModalProps={tutorialProps} openOnLoad={showTutorial} />
				</div>
			</TitleBar>
			{showTabs && (
				<TitleBarTabs
					currentTab={currentTab}
					setCurrentTab={setCurrentTab}
					tabs={tabs}
					navClassName="applications-tab-nav"
				/>
			)}

			<MainSectionTable>
				{currentTab == ApplicationTabs.OnboardingApplication && (
					<Applications
						limit={40}
						fullHeight={true}
						enableTitle={false}
						setNonDeniedBusinessApplicationsNum={setNonDeniedBusinessApplicationsNum}
						setNonDeniedIndividualApplicationsNum={setNonDeniedIndividualApplicationsNum}
						includedColumns={[
							ApplicationsColumns.id,
							ApplicationsColumns.type,
							...(useIsUnitUser() ? [ApplicationsColumns.org] : []),
							ApplicationsColumns.name,
							ApplicationsColumns.status,
							...(useIsUnitUser() ? [ApplicationsColumns.evaluation] : []),
							ApplicationsColumns.createdAt,
							ApplicationsColumns.updatedAt,
						]}
						refreshToken={token}
						showTabsOnHeader={showTabs}
					/>
				)}
				{canAccessProductApps && currentTab == ApplicationTabs.ProductApplication && (
					<ProductApplications
						limit={40}
						fullHeight={true}
						enableTitle={false}
						includedColumns={[
							ProductApplicationsColumns.id,
							ProductApplicationsColumns.type,
							...(useIsUnitUser() || useIsBankUser() ? [ProductApplicationsColumns.org] : []),
							ProductApplicationsColumns.name,
							ProductApplicationsColumns.customerId,
							ProductApplicationsColumns.status,
							ProductApplicationsColumns.createdAt,
							ProductApplicationsColumns.updatedAt,
						]}
						refreshToken={token}
						showTabsOnHeader={showTabs}
					/>
				)}
				{canAccessUnifiedApps && currentTab == ApplicationTabs.UnifiedApplication && (
					<UnifiedApplications
						limit={40}
						fullHeight={true}
						enableTitle={false}
						includedColumns={[
							UnifiedApplicationsColumns.id,
							UnifiedApplicationsColumns.type,
							UnifiedApplicationsColumns.org,
							UnifiedApplicationsColumns.contactEmail,
							UnifiedApplicationsColumns.status,
							UnifiedApplicationsColumns.unitApplication,
							UnifiedApplicationsColumns.processorApplication,
							UnifiedApplicationsColumns.createdAt,
						]}
						refreshToken={token}
						showTabsOnHeader={showTabs}
					/>
				)}
			</MainSectionTable>
		</div>
	)
}
